/**
 * Shipping rates composable
 * ------------------------------------------------------------------------------
 * Fetches delivery rates via Storefront API: cart create, add line, add address,
 * query deliveryGroups(withCarrierRates). Used on PDP when location is known.
 */

import axios from 'axios'
import Cookies from 'js-cookie'

import { storefrontRequestConfig, STOREFRONT_URL } from '@/js/utils/api'

/**
 * Parse address from location_preference cookie (e.g. "Sydney, NSW 2000")
 * @returns {{ city: string, provinceCode: string, zip: string } | null}
 */
function parseLocationFromCookie () {
  const locationPref = Cookies.get('location_preference')
  const postalCode = Cookies.get('postal_code')
  const state = Cookies.get('state')

  if (!postalCode || !state) return null

  let city = ''
  try {
    const decoded = locationPref ? decodeURIComponent(locationPref) : ''
    const parts = decoded.split(',')
    city = parts[0]?.trim() || ''
  } catch {
    city = ''
  }

  return {
    address1: city || 'Address',
    city: city || 'Sydney',
    provinceCode: state,
    zip: postalCode,
    countryCode: 'AU'
  }
}

/**
 * Create Storefront API cart with variant
 */
async function createCartWithVariant (variantId) {
  const mutation = `
    mutation CartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { id }
        userErrors { message field code }
      }
    }
  `
  const input = {
    lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }]
  }

  const config = {
    ...storefrontRequestConfig,
    headers: {
      ...storefrontRequestConfig.headers,
      'Content-Type': 'application/json'
    }
  }

  const { data } = await axios.post(STOREFRONT_URL, {
    query: mutation,
    variables: { input }
  }, config)

  const errors = data?.data?.cartCreate?.userErrors
  if (errors?.length) {
    throw new Error(errors.map(e => e.message).join(', '))
  }

  return data?.data?.cartCreate?.cart?.id
}

/**
 * Add delivery address to cart
 */
async function addDeliveryAddress (cartId, address) {
  const mutation = `
    mutation CartDeliveryAddressesAdd($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
      cartDeliveryAddressesAdd(cartId: $cartId, addresses: $addresses) {
        cart { id }
        userErrors { message field code }
      }
    }
  `
  const addresses = [{
    selected: true,
    address: {
      deliveryAddress: {
        address1: address.address1,
        city: address.city,
        provinceCode: address.provinceCode,
        zip: address.zip,
        countryCode: address.countryCode
      }
    }
  }]

  const config = {
    ...storefrontRequestConfig,
    headers: {
      ...storefrontRequestConfig.headers,
      'Content-Type': 'application/json'
    }
  }

  const { data } = await axios.post(STOREFRONT_URL, {
    query: mutation,
    variables: { cartId, addresses }
  }, config)

  const errors = data?.data?.cartDeliveryAddressesAdd?.userErrors
  if (errors?.length) {
    throw new Error(errors.map(e => e.message).join(', '))
  }

  return data?.data?.cartDeliveryAddressesAdd?.cart?.id
}

/**
 * Parse multipart/mixed GraphQL response (Incremental Delivery) and return merged cart.
 * @param {string} body - Raw response body
 * @param {string} contentType - Content-Type header value
 * @returns {{ deliveryGroups?: { edges: Array<{ node: { deliveryOptions?: Array } }> } } | null}
 */
function parseDeferredCartResponse (body, contentType) {
  let cart = null
  const boundaryMatch = contentType && contentType.match(/boundary=(\S+)/)
  const boundary = boundaryMatch ? boundaryMatch[1].replace(/;$/, '').trim() : 'graphql'

  if (!body || typeof body !== 'string') return null

  const parts = body.split(new RegExp(`--${boundary.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&')}\\s*`, 'g'))
  for (const part of parts) {
    const trimmed = part.trim()
    if (!trimmed || trimmed === '--') continue
    const jsonStart = trimmed.indexOf('{')
    if (jsonStart === -1) continue
    try {
      const json = JSON.parse(trimmed.slice(jsonStart))
      if (json.data?.cart) {
        cart = cart ? { ...cart, ...json.data.cart } : json.data.cart
      }
      if (json.incremental && Array.isArray(json.incremental)) {
        for (const inc of json.incremental) {
          if (inc.path && Array.isArray(inc.path) && inc.path.join('.') === 'cart' && inc.data) {
            cart = cart ? { ...cart, ...inc.data } : inc.data
          }
        }
      }
    } catch {
      // skip unparseable parts
    }
  }
  return cart
}

/**
 * Extract delivery options from cart (from either nodes or edges).
 * @param {{ deliveryGroups?: { nodes?: Array, edges?: Array<{ node?: { deliveryOptions?: Array } }> } }} cart
 * @returns {Array}
 */
function deliveryOptionsFromCart (cart) {
  if (!cart?.deliveryGroups) return []
  const dg = cart.deliveryGroups
  const nodes = dg.nodes || (dg.edges || []).map(e => e?.node).filter(Boolean)
  return nodes.flatMap(n => n.deliveryOptions || [])
}

/**
 * Query cart delivery groups with carrier rates.
 * withCarrierRates: true requires @defer; response may be multipart/mixed or single JSON.
 */
async function fetchCartDeliveryRates (cartId) {
  const query = `
    query CartDeliveryRates($cartId: ID!) {
      cart(id: $cartId) {
        id
        ... DeliveryGroups @defer
      }
    }
    fragment DeliveryGroups on Cart {
      deliveryGroups(first: 10, withCarrierRates: true) {
        edges {
          node {
            id
            deliveryOptions {
              handle
              title
              code
              estimatedCost {
                amount
                currencyCode
              }
              deliveryMethodType
            }
          }
        }
      }
    }
  `

  const config = {
    ...storefrontRequestConfig,
    headers: {
      ...storefrontRequestConfig.headers,
      'Content-Type': 'application/json',
      Accept: 'multipart/mixed; boundary=graphql'
    },
    responseType: 'text',
    transformResponse: [(data) => data]
  }

  const response = await axios.post(STOREFRONT_URL, {
    query,
    variables: { cartId }
  }, config)

  const contentType = (response.headers && response.headers['content-type']) || ''
  const body = response.data

  let cart = null
  if (contentType.includes('multipart/mixed')) {
    cart = parseDeferredCartResponse(body, contentType)
  } else {
    try {
      const parsed = typeof body === 'string' ? JSON.parse(body) : body
      cart = parsed?.data?.cart || null
      if (!cart && typeof body === 'string') {
        cart = parseDeferredCartResponse(body, contentType)
      }
    } catch {
      if (typeof body === 'string') cart = parseDeferredCartResponse(body, contentType)
    }
  }

  const options = deliveryOptionsFromCart(cart)
  console.log('options', options)
  if (!options.length) return []
  return options.map(opt => ({
    handle: opt.handle,
    title: opt.title || opt.code || 'Delivery',
    amount: opt.estimatedCost?.amount,
    currencyCode: opt.estimatedCost?.currencyCode || 'AUD',
    deliveryMethodType: opt.deliveryMethodType
  }))
}

/**
 * Fetch shipping rates for a variant at the current location
 * Creates a temporary cart, adds address, queries deliveryGroups
 * @param {number} variantId - Product variant ID (numeric)
 * @returns {Promise<Array<{ title: string, amount: string, currencyCode: string }>>}
 */
export async function fetchShippingRates (variantId) {
  const address = parseLocationFromCookie()
  if (!address) return []

  try {
    const cartId = await createCartWithVariant(variantId)
    if (!cartId) return []

    await addDeliveryAddress(cartId, address)
    const rates = await fetchCartDeliveryRates(cartId)
    return rates
  } catch (err) {
    console.warn('Shipping rates fetch error:', err)
    return []
  }
}
